/**
 * metadataCriteria.ts — pure, dependency-free pre-publish metadata evaluator.
 *
 * Client-side port of the backend metadata contract used for the universal
 * pre-publish warning gate. Block-tier findings are still overridable; they
 * require written sign-off in the dialog. Numeric bands and
 * regex literals are transcribed VERBATIM from:
 *   - backend/template_content_crew/orchestration/postgen_metadata.py  (PGM)
 *   - backend/template_content_crew/orchestration/postgen_social.py    (PGS)
 *   - backend/template_content_crew/seo/meta.py                        (META)
 * per the consolidated contract
 * ai-docs/scratchpad/prepublish-metadata-gate/criteria-contract-20260711.md.
 *
 * Implements rows 1-33 + 35 + 36 (client-checkable) plus the Issue-log delta
 * rows 39/40/41 and the Gap-4 stray-dash info warning.
 *
 * SKIPPED (need external site/robots/body data, out of scope for a pure
 * client evaluator): row 34 (slug collision with existing page), row 37 (draft
 * noindex proof), row 38 (listicle count vs body). Their ruleIds would be:
 *   'slug_collision' (34), 'draft_noindex' (37), 'listicle_count' (38).
 *
 * Frontend semantics note (deviation, documented): og_* / twitter_* fields are
 * OPTIONAL publish-time overrides — an empty value falls back to meta_title /
 * meta_description at publish (see ContentDetail field docs). Postgen AUTHORS
 * these fields so it treats empty as a failure; the gate does NOT — it only
 * evaluates OG/Twitter rows when the field is populated, to avoid warning on
 * every article that simply relies on the fallback. Twitter is already
 * "may be empty" in the contract (rows 28/30).
 */

// ---------------------------------------------------------------------------
// Issue category — defined here (the lowest-level module in the gate chain)
// to avoid a circular type import: metadataCriteria → issueSummary →
// prePublishGate → metadataCriteria. issueSummary.ts re-exports it so
// consumers import from either location.
// ---------------------------------------------------------------------------
export type IssueCategory =
  | 'contract' // metadataCriteria rules: pipe/length/keyword/brand/artifact/…
  | 'provenance' // readPostgenEvidence: value is a template fallback / failed run
  | 'accessibility' // missing image alt (non-decorative)
  | 'human' // human-QA / human-verify sign-off
  | 'pipeline' // backend status failed
  | 'uniqueness'; // slug collision

// ---------------------------------------------------------------------------
// Numeric constants (verbatim, contract Notes L79 / PGM L78-81, PGS L49-53)
// ---------------------------------------------------------------------------
export const META_TITLE_MIN = 50;
export const META_TITLE_MAX = 60;
export const META_DESCRIPTION_MIN = 140;
export const META_DESCRIPTION_MAX = 160;
export const OG_TITLE_MAX = 60;
export const OG_DESCRIPTION_MIN = 110;
export const OG_DESCRIPTION_MAX = 200;
export const TWITTER_TITLE_MAX = 70;
export const TWITTER_DESCRIPTION_MAX = 200;

export const META_TITLE_PIPE_SEPARATOR = ' | ';

// ---------------------------------------------------------------------------
// Regexes ported verbatim from seo/meta.py + postgen_*.py
// ---------------------------------------------------------------------------

// META `_ARTIFACT_TITLE_RE` (meta.py L785, re.VERBOSE|IGNORECASE) — flattened.
export const ARTIFACT_TITLE_RE = new RegExp(
  [
    '-\\d{8}', // date suffix like -20260513
    '-qa-\\d+', // qa run suffix
    '\\b[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}\\b', // UUID
    '\\brerun\\b',
    '\\brun_id\\b',
    '\\brun[-_]\\d+\\b',
    '\\[draft\\]',
    '\\(draft\\)',
    '\\bdraft[-_]\\d+\\b',
    '\\[\\s*agent[\\s_-]*sdk(?:[\\s_-]+draft)?\\s*\\]',
    '\\[\\s*agent[\\s_-]*sdk[\\s_-]*draft\\s*\\]',
    '\\bagent[\\s_-]?sdk[\\s_-]?draft\\b',
    '\\bagent[\\s_-]?sdk\\b',
    '\\[\\s*qa\\b[^\\]]*\\]',
    '\\[\\s*proved\\s*\\]',
    '\\bproved(?:[-_]\\d+)?\\b',
    '\\biter(?:ation)?[-_]?\\d+\\b',
    '\\b(?:claude|gpt|gemini|kimi|opus|sonnet|haiku|llama|mistral|grok)[-_]?(?:\\d|preview|turbo)',
    '\\bmodel[-_](?:name|id)\\b',
  ].join('|'),
  'i',
);

// META `_FORBIDDEN_TITLE_CHARS_RE` (meta.py L815). Apostrophe intentionally allowed.
export const FORBIDDEN_TITLE_CHARS_RE = /[[\]{}"`]/;

// PGM `_TITLE_DASH_SEPARATOR_RE` (postgen_metadata.py L93). Also used for the
// Gap-4 stray ' - ' dash-in-meta_title info warning.
export const TITLE_DASH_SEPARATOR_RE = /\s[-‐-―]\s/;

// PGM description hygiene (postgen_metadata.py L94-95)
export const DESCRIPTION_URL_RE = /https?:\/\/|www\./i;
export const DESCRIPTION_MARKDOWN_RE = /`|\*\*|__|\[[^\]]+\]\([^)]*\)/;

// PGS social hygiene (postgen_social.py L62-72)
export const SOCIAL_URL_RE =
  /\b(?:https?:\/\/|www\.|[a-z0-9](?:[a-z0-9-]{0,61}[a-z0-9])?\.(?:com|net|org|io|co|us|biz|info|edu|gov)(?:\/[^\s]*)?)/i;
export const SOCIAL_MARKDOWN_RE =
  /(\*\*|__|~~|(?<!\*)\*[^*\n]+\*(?!\*)|(?<!_)_[^_\n]+_(?!_)|^\s{0,3}#{1,6}\s|^\s{0,3}[-*+]\s|^\s{0,3}>\s|\[[^\]]+\]\([^)]+\))/m;

// Rule 39 (Issue-log delta): rating-widget / excerpt leak in og_description.
export const RATING_WIDGET_LEAK_RE = /\b\d+(?:\.\d+)?\/5\b|\(\s*\d+\s+Reviews?\s*\)/i;

// ---------------------------------------------------------------------------
// is_complete_meta_description battery (meta.py L397-517) — full parity port.
// ---------------------------------------------------------------------------
const DANGLING_META_END_WORDS = [
  'a', 'an', 'and', 'as', 'at', 'because', 'before', 'by', 'commercial',
  'contact', 'define', 'for', 'from', 'in', 'lasting', 'of', 'on', 'or', 'our',
  'protect', 'that', 'the', 'to', 'when', 'while', 'with', 'your', 'enjoy',
  'experience', 'receive',
];

const byLenDesc = (words: string[]): string[] =>
  [...words].sort((a, b) => b.length - a.length || (a < b ? -1 : 1));

const INCOMPLETE_META_END_RE = new RegExp(
  `\\b(?:${byLenDesc(DANGLING_META_END_WORDS).join('|')})\\.$`,
  'i',
);
const INCOMPLETE_META_VERB_PHRASE_RE =
  /\b(?:when to call|how to choose|what to know about|what to expect from)\s+(?:certified|licensed|professional|local|trusted|experienced|qualified)?\.$/i;
const INCOMPLETE_META_WH_NOUN_RE = new RegExp(
  '\\b(?:what|which)\\s+' +
    '(?!(?:to|you|they|we|' +
    '(?:(?:every|each|any|all|your|their|the|a)\\s+)?' +
    '(?:homeowner|homeowners|business|businesses|contractor|contractors|' +
    'owner|owners|team|teams|crew|crews|operator|operators|buyer|buyers|' +
    'manager|managers|facility|facilities))\\s+' +
    '(?:need|needs|can|should|expect|know|cost|costs|include|includes|' +
    'require|requires|mean|means|get|avoid|choose|compare|plan)\\b)' +
    "[a-z][a-z'-]*(?:\\s+[a-z][a-z'-]*){0,4}\\.$",
  'i',
);
const INCOMPLETE_META_ARTICLE_ADJECTIVE_RE =
  /\b(?:a|an|the)\s+(?:better|clean|comfortable|durable|easy|functional|lasting|professional|qualified|reliable|safe|seamless|smooth|stress-free|trusted|worry-free)\.$/i;
const INCOMPLETE_META_COORDINATED_MODIFIER_RE =
  /\b(?:and|or)\s+(?:better|clean|comfortable|easy|reliable|safe|seamless|smooth|stress-free|worry-free)\s+(?:experience|home|process|project|result|results|service|solution|year)\.$/i;
const INCOMPLETE_META_WITH_DANGLING_MODIFIER_RE =
  /\bwith\s+(?:[a-z][a-z'-]*\s+){0,6}(?:and|or)\s+(?:custom|expert|personalized|professional|reliable|seamless|stress-free|tailored|worry-free)\.$/i;

/** META `_clean_meta_text` (meta.py L285): collapse whitespace, strip wrappers. */
export function cleanMetaText(text: string | null | undefined): string {
  const collapsed = String(text ?? '').replace(/\s+/g, ' ').trim();
  return collapsed.replace(/^[`"']+/, '').replace(/[`"']+$/, '');
}

/** META `is_complete_meta_description` (meta.py L494). */
export function isCompleteMetaDescription(text: string | null | undefined): boolean {
  const description = cleanMetaText(text);
  if (!description) return false;
  if (description.endsWith('...')) return false;
  const last = description[description.length - 1];
  if (last !== '.' && last !== '!' && last !== '?') return false;
  if (INCOMPLETE_META_END_RE.test(description)) return false;
  if (INCOMPLETE_META_VERB_PHRASE_RE.test(description)) return false;
  if (INCOMPLETE_META_ARTICLE_ADJECTIVE_RE.test(description)) return false;
  if (INCOMPLETE_META_COORDINATED_MODIFIER_RE.test(description)) return false;
  if (INCOMPLETE_META_WITH_DANGLING_MODIFIER_RE.test(description)) return false;
  return !INCOMPLETE_META_WH_NOUN_RE.test(description);
}

// ---------------------------------------------------------------------------
// Keyword-phrase matching (`_contains_phrase`, meta.py L291-329)
// ---------------------------------------------------------------------------
const WORD_RE = /[a-z0-9]+(?:'[a-z0-9]+)?/gi;

function normalizeKeywordWord(word: string): string {
  const w = (word ?? '').toLowerCase().replace(/^[.,;:!?"'()[\]{}]+|[.,;:!?"'()[\]{}]+$/g, '');
  if (w.length <= 2) return w;
  if (w.endsWith('ies') && w.length > 4) return w.slice(0, -3) + 'y';
  if (w.endsWith('es') && w.length > 3) {
    const base = w.slice(0, -2);
    if (/(x|ch|sh|s|z)$/.test(base)) return base;
  }
  if (w.endsWith('s') && !w.endsWith('ss')) return w.slice(0, -1);
  return w;
}

function findWords(text: string): string[] {
  return (text ?? '').match(WORD_RE) ?? [];
}

function keywordWordsInText(phrase: string | null | undefined, text: string): boolean {
  if (!phrase) return true;
  const keywordWords = findWords(phrase).map(normalizeKeywordWord).filter(Boolean);
  if (keywordWords.length === 0) return true;
  const textWords = new Set(findWords(text).map(normalizeKeywordWord));
  return keywordWords.every((w) => textWords.has(w));
}

/** META `_contains_phrase`: substring OR all keyword tokens present. */
export function containsPhrase(text: string, phrase: string | null | undefined): boolean {
  if (!phrase) return true;
  const lowered = (text ?? '').toLowerCase();
  return lowered.includes(phrase.toLowerCase()) || keywordWordsInText(phrase, text);
}

// ---------------------------------------------------------------------------
// Brand matching (`contains_brand_variant`, meta.py L332-360)
// ---------------------------------------------------------------------------
const LEGAL_SUFFIX_RE =
  /,?\s*\b(LLC|Inc\.?|Ltd\.?|Limited|Co\.?|Company|Corp\.?|Corporation|LLP|LP)\b\.?/gi;

function brandVariants(brandName: string | null | undefined): string[] {
  const brand = cleanMetaText(brandName ?? '');
  if (!brand) return [];
  const stripped = brand.replace(LEGAL_SUFFIX_RE, '').trim();
  const variants = [brand];
  if (stripped && stripped.toLowerCase() !== brand.toLowerCase()) variants.push(stripped);
  return variants;
}

/** META `contains_brand_variant`: brand OR legal-suffix-stripped brand present. */
export function containsBrandVariant(text: string, brandName: string | null | undefined): boolean {
  const variants = brandVariants(brandName);
  if (variants.length === 0) return true; // no brand datum -> cannot fail
  const lowered = (text ?? '').toLowerCase();
  return variants.some((v) => lowered.includes(v.toLowerCase()));
}

// ---------------------------------------------------------------------------
// H1 / title helpers (postgen_metadata.py L650-726)
// ---------------------------------------------------------------------------
const TITLE_TOKEN_RE = /[a-z0-9]+/g;
const TITLE_WORD_RE = /[A-Za-z0-9][A-Za-z0-9']*/g;

const TITLE_CASE_MINOR_WORDS = new Set([
  'a', 'an', 'and', 'as', 'at', 'but', 'by', 'for', 'from', 'in', 'nor', 'of',
  'on', 'or', 'per', 'the', 'to', 'vs', 'via', 'with',
]);
const TITLE_LOCATION_PREFIXES = new Set(['in', 'near', 'around', 'throughout', 'across']);
const TITLE_STRUCTURE_CUE_WORDS = new Set([
  'a', 'about', 'and', 'before', 'choose', 'choosing', 'complete', 'cost',
  'costs', 'expect', 'explained', 'facts', 'factors', 'for', 'guide', 'how',
  'key', 'know', 'matter', 'next', 'pricing', 'quick', 'reasons', 'should',
  'signs', 'steps', 'timeline', 'tips', 'to', 'what', 'why',
]);
const TITLE_LOCATION_HINT_WORDS = new Set([
  'alabama', 'alaska', 'arizona', 'arkansas', 'area', 'california', 'carolina',
  'city', 'colorado', 'connecticut', 'county', 'dakota', 'delaware', 'district',
  'florida', 'georgia', 'hawaii', 'idaho', 'illinois', 'indiana', 'iowa',
  'kansas', 'kentucky', 'louisiana', 'maine', 'maryland', 'massachusetts', 'me',
  'metro', 'michigan', 'minnesota', 'mississippi', 'missouri', 'montana',
  'nebraska', 'nevada', 'north', 'northern', 'ohio', 'oklahoma', 'oregon',
  'pennsylvania', 'region', 'rhode', 'south', 'southern', 'tennessee', 'texas',
  'utah', 'vermont', 'virginia', 'washington', 'west', 'wisconsin', 'wyoming',
]);
const TITLE_STATE_ABBREVIATIONS = new Set([
  'al', 'ak', 'az', 'ar', 'ca', 'co', 'ct', 'de', 'dc', 'fl', 'ga', 'hi', 'id',
  'il', 'in', 'ia', 'ks', 'ky', 'la', 'me', 'md', 'ma', 'mi', 'mn', 'ms', 'mo',
  'mt', 'ne', 'nv', 'nh', 'nj', 'nm', 'ny', 'nc', 'nd', 'oh', 'ok', 'or', 'pa',
  'ri', 'sc', 'sd', 'tn', 'tx', 'ut', 'vt', 'va', 'wa', 'wv', 'wi', 'wy',
]);

function titleTokens(value: string): string[] {
  return (String(value).toLowerCase().match(TITLE_TOKEN_RE) ?? []);
}
function titleNormalizedPhrase(value: string): string {
  return titleTokens(value).join(' ');
}
function titleEqualsKeyword(title: string, keyword: string): boolean {
  const t = titleNormalizedPhrase(title);
  const k = titleNormalizedPhrase(keyword);
  return Boolean(t && k && t === k);
}

/** PGM `_is_title_case` (postgen_metadata.py L708). */
export function isTitleCase(title: string): boolean {
  const words = String(title).match(TITLE_WORD_RE) ?? [];
  if (words.length === 0) return false;
  const lastIndex = words.length - 1;
  for (let index = 0; index < words.length; index++) {
    const word = words[index];
    if (/\d/.test(word)) continue;
    if (word.length > 1 && word === word.toUpperCase()) continue;
    const lower = word.toLowerCase();
    if (index > 0 && index < lastIndex && TITLE_CASE_MINOR_WORDS.has(lower)) continue;
    const firstAlpha = [...word].find((c) => /[a-z]/i.test(c)) ?? '';
    if (!firstAlpha || firstAlpha !== firstAlpha.toUpperCase()) return false;
  }
  return true;
}

function looksLikeTrivialLocationSuffix(suffixTokens: string[], afterLocationPrefix: boolean): boolean {
  const tokens = suffixTokens.filter((t) => t !== 'the');
  if (tokens.length === 0 || tokens.length > 5) return false;
  if (tokens.some((t) => TITLE_STRUCTURE_CUE_WORDS.has(t))) return false;
  const hasHint = tokens.some((t) => TITLE_LOCATION_HINT_WORDS.has(t) || TITLE_STATE_ABBREVIATIONS.has(t));
  if (hasHint) return true;
  return afterLocationPrefix && tokens.every((t) => /^[a-z]+$/.test(t));
}

/** PGM `_title_is_keyword_plus_trivial_location_suffix` (postgen_metadata.py L688). */
function titleIsKeywordPlusTrivialLocationSuffix(title: string, keyword: string): boolean {
  const keywordTok = titleTokens(keyword);
  const titleTok = titleTokens(title);
  if (
    keywordTok.length === 0 ||
    titleTok.length <= keywordTok.length ||
    keywordTok.some((tk, i) => titleTok[i] !== tk)
  ) {
    return false;
  }
  const suffix = titleTok.slice(keywordTok.length);
  if (suffix.length === 2 && suffix[0] === 'near' && suffix[1] === 'me') return true;
  if (suffix.length > 0 && TITLE_LOCATION_PREFIXES.has(suffix[0])) {
    return looksLikeTrivialLocationSuffix(suffix.slice(1), true);
  }
  return looksLikeTrivialLocationSuffix(suffix, false);
}

// META incomplete-title-phrase battery (meta.py L860-892) — for row 7.
const TRAILING_TITLE_EDGE_WORDS = [
  'a', 'an', 'and', 'as', 'at', 'by', 'for', 'from', 'in', 'into', 'of', 'on',
  'or', 'that', 'the', 'to', 'vs', 'versus', 'with', 'without',
];
const TRAILING_TITLE_MODIFIER_WORDS = [
  'affordable', 'better', 'complete', 'custom', 'durable', 'easy', 'every',
  'expert', 'lasting', 'local', 'modern', 'personalized', 'professional',
  'reliable', 'safe', 'seamless', 'smooth', 'stress-free', 'trusted',
  'worry-free', 'your',
];
const TRAILING_TITLE_TERMINAL_WORDS = ['built', 'crafted', 'designed', 'made', 'tailored'];

const INCOMPLETE_TITLE_PHRASE_RE =
  /(?:\b(?:find|choose|select|pick|get)\s+(?:the\s+)?(?:right|best|perfect|ideal)|\b(?:what|why|when|where|how))\s*$/i;
const INCOMPLETE_TITLE_MODIFIER_TAIL_RE = new RegExp(
  `\\b(?:${byLenDesc(TRAILING_TITLE_EDGE_WORDS).join('|')})\\s+(?:${byLenDesc(TRAILING_TITLE_MODIFIER_WORDS).join('|')})\\s*$`,
  'i',
);
const INCOMPLETE_TITLE_EDGE_RE = new RegExp(
  `\\b(?:${byLenDesc(TRAILING_TITLE_EDGE_WORDS).join('|')})\\s*$`,
  'i',
);
const INCOMPLETE_TITLE_TERMINAL_RE = new RegExp(
  `\\b(?:${byLenDesc(TRAILING_TITLE_TERMINAL_WORDS).join('|')})\\s*$`,
  'i',
);

function titleKeywordPart(title: string): string {
  if (title.includes(META_TITLE_PIPE_SEPARATOR)) {
    return title.split(META_TITLE_PIPE_SEPARATOR)[0].trim();
  }
  return title.trim();
}

/** META `_has_incomplete_title_phrase` (meta.py L927). */
export function hasIncompleteTitlePhrase(title: string): boolean {
  const kp = titleKeywordPart(title);
  return (
    INCOMPLETE_TITLE_PHRASE_RE.test(kp) ||
    INCOMPLETE_TITLE_MODIFIER_TAIL_RE.test(kp) ||
    INCOMPLETE_TITLE_EDGE_RE.test(kp) ||
    INCOMPLETE_TITLE_TERMINAL_RE.test(kp)
  );
}

// ---------------------------------------------------------------------------
// Public API
// ---------------------------------------------------------------------------
export type MetadataTier = 'info' | 'warn' | 'block';
export type MetadataSeverity = MetadataTier;

export interface MetadataViolation {
  /** Stable identifier, e.g. 'meta_title_length'. */
  ruleId: string;
  /** Content field the violation is about, e.g. 'meta_title', 'url_slug'. */
  field: string;
  /** Gate tier. Block still means overridable, but requires written sign-off. */
  tier: MetadataTier;
  /** Back-compat alias for existing gate/UI code. Mirrors `tier`. */
  severity: MetadataSeverity;
  message: string;
  /** Contract row / source pointer for traceability. */
  sourceNote?: string;
  /**
   * Issue TYPE bucket for the unified badge / grouped dialog. Optional for
   * back-compat — violations without a category default to 'contract' in
   * normalizeViolation. Set explicitly by buildGateItem for non-contract
   * sources (provenance, accessibility, human, pipeline, uniqueness).
   */
  category?: IssueCategory;
}

type MetadataViolationDraft = Omit<MetadataViolation, 'tier'> & {
  tier?: MetadataTier;
};

function normalizeViolation(violation: MetadataViolationDraft): MetadataViolation {
  const tier = violation.tier ?? violation.severity;
  return {
    ...violation,
    tier,
    severity: tier,
    category: violation.category ?? 'contract',
  };
}

export interface MetadataCriteriaInput {
  metaTitle?: string | null;
  metaDescription?: string | null;
  /** The on-page H1 / post title. */
  title?: string | null;
  urlSlug?: string | null;
  ogTitle?: string | null;
  ogDescription?: string | null;
  twitterTitle?: string | null;
  twitterDescription?: string | null;
  /** Yoast focus keyword (row 35). */
  focusKeyword?: string | null;
  /** Target keyword phrase for keyword-content rules (rows 3/13/14/19/41). */
  keyword?: string | null;
  /** Tenant brand/business name (rows 4/12/18/26); brand rules skip when absent. */
  brandName?: string | null;
}

const has = (v: string | null | undefined): v is string => typeof v === 'string' && v.trim().length > 0;

/** Rule 33 slug artifact detection (contract row 33 — warn). */
function slugArtifactMessage(slug: string): string | null {
  const s = slug.trim();
  // ?p=ID auto-ID permalink
  if (/[?&]p=\d+/i.test(s)) return 'URL slug uses an auto-generated "?p=ID" permalink instead of a clean keyword slug.';
  // -qa-YYYYMMDD / -N-qa-date workflow suffix (check before generic -N)
  if (/-qa-\d{6,8}\b/i.test(s) || /\bqa-\d{6,8}\b/i.test(s)) return 'URL slug contains a "-qa-<date>" QA workflow suffix.';
  if (/-\d{8}(?:$|[/?#-])/.test(s)) return 'URL slug contains a "-YYYYMMDD" date artifact.';
  // trailing -N numbered suffix (e.g. /...-6, /...-3) after the final path segment
  const lastSegment = s.replace(/[?#].*$/, '').replace(/\/+$/, '').split('/').pop() ?? s;
  if (/-\d{1,3}$/.test(lastSegment)) return 'URL slug ends with a "-N" numbered suffix (possible cannibalization / auto-dedupe).';
  return null;
}

/**
 * Rule 36 — H1 <-> meta_title alignment. CLIENT-SIDE APPROXIMATION of the
 * backend `verify_h1_meta_title_alignment` (which is not enforced by postgen).
 *
 * Heuristic (documented): compare the H1 against the meta_title's KEYWORD part
 * (the segment before the ` | ` brand). Reduce both to "significant" tokens
 * (length > 2, not a title-case minor/stopword). Flag a warn when:
 *   (a) both sides HAVE significant tokens but share NONE (topics diverge), OR
 *   (b) a keyword is provided and the two fields do not share any keyword token
 *       (they are not anchored on the same primary keyword).
 * This is a coarse token-overlap check, deliberately lenient to avoid noise on
 * legitimately-varied phrasings; it only fires on clear divergence.
 */
function h1MetaAlignmentMessage(title: string, metaTitle: string, keyword?: string | null): string | null {
  const significant = (v: string): Set<string> =>
    new Set(titleTokens(v).filter((t) => t.length > 2 && !TITLE_CASE_MINOR_WORDS.has(t)));
  const h1Tokens = significant(title);
  const mtTokens = significant(titleKeywordPart(metaTitle));
  if (h1Tokens.size === 0 || mtTokens.size === 0) return null;

  const shared = [...h1Tokens].filter((t) => mtTokens.has(t));
  if (shared.length === 0) {
    return 'H1 and meta title appear to describe different topics (no shared keywords).';
  }
  if (has(keyword)) {
    const kwTokens = significant(keyword);
    if (kwTokens.size > 0) {
      const h1HasKw = [...kwTokens].some((t) => h1Tokens.has(t));
      const mtHasKw = [...kwTokens].some((t) => mtTokens.has(t));
      if (!(h1HasKw && mtHasKw)) {
        return 'H1 and meta title are not both anchored on the primary keyword.';
      }
    }
  }
  return null;
}

/** Shared hygiene checks for social fields (PGS `_shared_field_failures`). */
function socialHygieneViolations(field: string, value: string, severity: MetadataSeverity, rowNote: string): MetadataViolationDraft[] {
  const out: MetadataViolationDraft[] = [];
  if (SOCIAL_URL_RE.test(value)) {
    out.push({ ruleId: `${field}_url`, field, severity, message: `${field} must not contain URLs or domain names.`, sourceNote: rowNote });
  }
  if (SOCIAL_MARKDOWN_RE.test(value)) {
    out.push({ ruleId: `${field}_markdown`, field, severity, message: `${field} must not contain markdown syntax.`, sourceNote: rowNote });
  }
  if (FORBIDDEN_TITLE_CHARS_RE.test(value)) {
    out.push({ ruleId: `${field}_forbidden_chars`, field, severity, message: `${field} must not contain any of these characters: [ ] { } " \`.`, sourceNote: rowNote });
  }
  if (ARTIFACT_TITLE_RE.test(value)) {
    out.push({ ruleId: `${field}_artifact`, field, severity, message: `${field} must not contain internal/QA artifact tokens (dates, UUIDs, run/draft/agent-sdk/model-name markers).`, sourceNote: rowNote });
  }
  return out;
}

/**
 * Evaluate the row-only + keyword + brand metadata criteria for a single
 * article. Returns an array of violations (empty === clean). Pure & synchronous.
 */
export function evaluateMetadataCriteria(input: MetadataCriteriaInput): MetadataViolation[] {
  const v: MetadataViolationDraft[] = [];
  const {
    metaTitle, metaDescription, title, urlSlug, ogTitle, ogDescription,
    twitterTitle, twitterDescription, focusKeyword, keyword, brandName,
  } = input;
  const brandAvailable = has(brandName);

  // ---- meta_title (rows 1-7, 41 + Gap-4) --------------------------------
  if (has(metaTitle)) {
    const mt = metaTitle;
    const len = mt.length;
    if (len < META_TITLE_MIN || len > META_TITLE_MAX) {
      v.push({ ruleId: 'meta_title_length', field: 'meta_title', severity: 'warn', message: `Meta title is ${len} characters; aim for ${META_TITLE_MIN}-${META_TITLE_MAX}.`, sourceNote: 'row 1 (PGM)' });
    }
    if (!mt.includes('|')) {
      v.push({ ruleId: 'meta_title_pipe', field: 'meta_title', severity: 'warn', message: 'Meta title should use a "|" pipe to separate the keyword and brand.', sourceNote: 'row 2 (PGM)' });
    }
    if (has(keyword) && !containsPhrase(mt, keyword)) {
      v.push({ ruleId: 'meta_title_keyword', field: 'meta_title', severity: 'warn', message: `Meta title should contain the keyword "${keyword}".`, sourceNote: 'row 3 (PGM)' });
    }
    if (brandAvailable && !containsBrandVariant(mt, brandName)) {
      v.push({ ruleId: 'meta_title_brand', field: 'meta_title', severity: 'warn', message: `Meta title should contain the brand name "${brandName}".`, sourceNote: 'row 4 (PGM)' });
    }
    if (FORBIDDEN_TITLE_CHARS_RE.test(mt)) {
      v.push({ ruleId: 'meta_title_forbidden_chars', field: 'meta_title', severity: 'warn', message: 'Meta title must not contain any of these characters: [ ] { } " `.', sourceNote: 'row 5 (PGM)' });
    }
    if (ARTIFACT_TITLE_RE.test(mt)) {
      v.push({ ruleId: 'meta_title_artifact', field: 'meta_title', severity: 'warn', message: 'Meta title contains an internal/QA artifact token (date, UUID, run/draft/agent-sdk/model-name marker).', sourceNote: 'row 6 (PGM)' });
    }
    if (hasIncompleteTitlePhrase(mt)) {
      v.push({ ruleId: 'meta_title_incomplete_phrase', field: 'meta_title', severity: 'warn', message: 'Meta title ends on a dangling CTA fragment (e.g. "Find the Right | Brand").', sourceNote: 'row 7 (PGM)' });
    }
    // Gap-4: stray ' - ' dash inside meta_title (pipe present but extra dash).
    if (TITLE_DASH_SEPARATOR_RE.test(mt)) {
      v.push({ ruleId: 'meta_title_stray_dash', field: 'meta_title', severity: 'info', message: 'Meta title contains a stray " - " dash separator; prefer a single "|" pipe.', sourceNote: 'Gap-4' });
    }
    // Row 41: keyword front-loading (keyword match starts in the first half).
    if (has(keyword)) {
      const idx = mt.toLowerCase().indexOf(keyword.toLowerCase());
      if (idx >= 0 && idx > Math.floor(mt.length / 2)) {
        v.push({ ruleId: 'meta_title_keyword_frontload', field: 'meta_title', severity: 'info', message: 'Keyword appears in the second half of the meta title; front-load it for better SERP weighting.', sourceNote: 'row 41 (CAM)' });
      }
    }
  } else {
    v.push({ ruleId: 'meta_title_present', field: 'meta_title', severity: 'block', message: 'Meta title is empty; it must carry to WordPress.', sourceNote: 'LOG-BLOCK' });
  }

  // ---- H1 / title (rows 8-14) ------------------------------------------
  if (has(title)) {
    const t = title;
    if (!isTitleCase(t)) {
      v.push({ ruleId: 'h1_title_case', field: 'title', severity: 'info', message: 'Title (H1) should use Title Case.', sourceNote: 'row 9 (PGM)' });
    }
    if (t.includes('|')) {
      v.push({ ruleId: 'h1_pipe', field: 'title', severity: 'block', message: 'Title (H1) must not contain a "|" pipe (brand/pipe belongs in the meta title only).', sourceNote: 'row 10 (PGM)' });
    }
    if (TITLE_DASH_SEPARATOR_RE.test(t)) {
      v.push({ ruleId: 'h1_dash', field: 'title', severity: 'block', message: 'Title (H1) must not use a dash separator.', sourceNote: 'row 11 (PGM)' });
    }
    if (brandAvailable && containsBrandVariant(t, brandName)) {
      v.push({ ruleId: 'h1_brand', field: 'title', severity: 'block', message: `Title (H1) should not contain the brand name "${brandName}".`, sourceNote: 'row 12 (PGM)' });
    }
    if (has(keyword) && titleEqualsKeyword(t, keyword)) {
      v.push({ ruleId: 'h1_equals_keyword', field: 'title', severity: 'block', message: 'Title (H1) is the raw target keyword; turn it into a natural headline.', sourceNote: 'row 13 (PGM)' });
    }
    if (has(keyword) && titleIsKeywordPlusTrivialLocationSuffix(t, keyword)) {
      v.push({ ruleId: 'h1_location_suffix', field: 'title', severity: 'warn', message: 'Title (H1) is the keyword plus a trivial location suffix ("near me" / "in <city>"); add real headline structure.', sourceNote: 'row 14 (PGM)' });
    }
  } else {
    // Row 8: H1 non-empty.
    v.push({ ruleId: 'h1_empty', field: 'title', severity: 'warn', message: 'Title (H1) is empty; every article needs an on-page headline.', sourceNote: 'row 8 (PGM)' });
  }

  // ---- meta_description (rows 15-21, 40) --------------------------------
  if (has(metaDescription)) {
    const md = metaDescription;
    const len = md.length;
    if (len < META_DESCRIPTION_MIN || len > META_DESCRIPTION_MAX) {
      v.push({ ruleId: 'meta_description_length', field: 'meta_description', severity: 'warn', message: `Meta description is ${len} characters; aim for ${META_DESCRIPTION_MIN}-${META_DESCRIPTION_MAX}.`, sourceNote: 'row 16 (PGM)' });
    }
    if (!isCompleteMetaDescription(md)) {
      v.push({ ruleId: 'meta_description_complete', field: 'meta_description', severity: 'warn', message: 'Meta description is not a complete sentence (hard-cut or dangling ending).', sourceNote: 'row 17 (PGM)' });
    }
    if (brandAvailable && !containsBrandVariant(md, brandName)) {
      v.push({ ruleId: 'meta_description_brand', field: 'meta_description', severity: 'warn', message: `Meta description should mention the brand name "${brandName}".`, sourceNote: 'row 18 (PGM)' });
    }
    if (has(keyword) && !containsPhrase(md, keyword)) {
      v.push({ ruleId: 'meta_description_keyword', field: 'meta_description', severity: 'warn', message: `Meta description should mention the keyword "${keyword}".`, sourceNote: 'row 19 (PGM)' });
    }
    if (DESCRIPTION_URL_RE.test(md)) {
      v.push({ ruleId: 'meta_description_url', field: 'meta_description', severity: 'warn', message: 'Meta description must not contain URLs.', sourceNote: 'row 20 (PGM)' });
    }
    if (DESCRIPTION_MARKDOWN_RE.test(md)) {
      v.push({ ruleId: 'meta_description_markdown', field: 'meta_description', severity: 'warn', message: 'Meta description must not contain markdown.', sourceNote: 'row 21 (PGM)' });
    }
    // Row 40: duplicated consecutive sentence.
    const sentences = md.split(/(?<=[.!?])\s+/).map((s) => s.trim().toLowerCase()).filter(Boolean);
    for (let i = 1; i < sentences.length; i++) {
      if (sentences[i] === sentences[i - 1]) {
        v.push({ ruleId: 'meta_description_duplicate_sentence', field: 'meta_description', severity: 'warn', message: 'Meta description repeats a sentence back-to-back.', sourceNote: 'row 40 (CAM)' });
        break;
      }
    }
  } else {
    // Row 15: meta_description present (hard blocker).
    v.push({ ruleId: 'meta_description_present', field: 'meta_description', severity: 'block', message: 'Meta description is empty; it must carry to WordPress.', sourceNote: 'row 15 (PGM)' });
  }

  // ---- og_title (rows 22-23) — evaluate only when populated --------------
  if (has(ogTitle)) {
    if (ogTitle.length > OG_TITLE_MAX) {
      v.push({ ruleId: 'og_title_length', field: 'og_title', severity: 'info', message: `OG title is ${ogTitle.length} characters; keep it at most ${OG_TITLE_MAX}.`, sourceNote: 'row 22 (PGS)' });
    }
    v.push(...socialHygieneViolations('og_title', ogTitle, 'info', 'row 23 (PGS)'));
  }

  // ---- og_description (rows 24-27, 39) — evaluate only when populated ----
  if (has(ogDescription)) {
    const od = ogDescription;
    if (od.length < OG_DESCRIPTION_MIN || od.length > OG_DESCRIPTION_MAX) {
      v.push({ ruleId: 'og_description_length', field: 'og_description', severity: 'info', message: `OG description is ${od.length} characters; aim for ${OG_DESCRIPTION_MIN}-${OG_DESCRIPTION_MAX}.`, sourceNote: 'row 24 (PGS)' });
    }
    if (!isCompleteMetaDescription(od)) {
      v.push({ ruleId: 'og_description_complete', field: 'og_description', severity: 'warn', message: 'OG description is not a complete sentence.', sourceNote: 'row 25 (PGS)' });
    }
    if (brandAvailable && !containsBrandVariant(od, brandName)) {
      v.push({ ruleId: 'og_description_brand', field: 'og_description', severity: 'warn', message: `OG description should mention the brand name "${brandName}".`, sourceNote: 'row 26 (PGS)' });
    }
    // Row 39: rating-widget / excerpt leak.
    if (RATING_WIDGET_LEAK_RE.test(od)) {
      v.push({ ruleId: 'og_description_rating_leak', field: 'og_description', severity: 'warn', message: 'OG description contains a rating-widget/excerpt leak (e.g. "0/5", "(0 Reviews)").', sourceNote: 'row 39 (LOG)' });
    }
    v.push(...socialHygieneViolations('og_description', od, 'info', 'row 27 (PGS)'));
  }

  // ---- twitter_title (rows 28-29) — evaluate only when populated --------
  if (has(twitterTitle)) {
    if (twitterTitle.length > TWITTER_TITLE_MAX) {
      v.push({ ruleId: 'twitter_title_length', field: 'twitter_title', severity: 'info', message: `Twitter title is ${twitterTitle.length} characters; keep it at most ${TWITTER_TITLE_MAX}.`, sourceNote: 'row 28 (PGS)' });
    }
    v.push(...socialHygieneViolations('twitter_title', twitterTitle, 'info', 'row 29 (PGS)'));
  }

  // ---- twitter_description (rows 30-32) — evaluate only when populated ---
  if (has(twitterDescription)) {
    const td = twitterDescription;
    if (td.length > TWITTER_DESCRIPTION_MAX) {
      v.push({ ruleId: 'twitter_description_length', field: 'twitter_description', severity: 'info', message: `Twitter description is ${td.length} characters; keep it at most ${TWITTER_DESCRIPTION_MAX}.`, sourceNote: 'row 30 (PGS)' });
    }
    if (!isCompleteMetaDescription(td)) {
      v.push({ ruleId: 'twitter_description_complete', field: 'twitter_description', severity: 'info', message: 'Twitter description is not a complete sentence.', sourceNote: 'row 31 (PGS)' });
    }
    v.push(...socialHygieneViolations('twitter_description', td, 'info', 'row 32 (PGS)'));
  }

  // ---- url_slug (row 33) ------------------------------------------------
  if (has(urlSlug)) {
    const msg = slugArtifactMessage(urlSlug);
    if (msg) {
      v.push({ ruleId: 'url_slug_artifact', field: 'url_slug', severity: 'block', message: msg, sourceNote: 'row 33 (B1/B2/LOG)' });
    }
  }

  // ---- focus_keyword (row 35) — info, only when field present -----------
  if (has(focusKeyword) && has(keyword) && !containsPhrase(focusKeyword, keyword)) {
    v.push({ ruleId: 'focus_keyword', field: 'focus_keyword', severity: 'info', message: 'Focus keyword should retain the full target keyword (including any "near me").', sourceNote: 'row 35 (CAM)' });
  }

  // ---- H1 <-> meta_title alignment (row 36) -----------------------------
  if (has(title) && has(metaTitle)) {
    const msg = h1MetaAlignmentMessage(title, metaTitle, keyword);
    if (msg) {
      v.push({ ruleId: 'h1_meta_alignment', field: 'meta_title', severity: 'warn', message: msg, sourceNote: 'row 36 (CAM, client heuristic)' });
    }
  }

  return v.map(normalizeViolation);
}
